import { useInstance } from '@meraki-internal/react-dependency-injection';
import { IQueryParamDrivenDrawerContentProvider } from '../../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { CareMapState, ICareMapOnboardingStep } from '../../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { AlertPresenter } from '../../AlertBinder';
import { useSubscriptionSync } from '../../support/state/useSubscriptionV2';
import { QueryParamDrivenDrawerPage } from '../../components/CareMapDrawerPage';
import { IonButton } from '@ionic/react';
import { ActiveCaremapNoteCreator } from './ActiveCaremapNoteCreator';

interface IAddNoteDrawerProps {
    autoOpenFileChooser?: boolean;
    careMapOnboardingStepComplete?: ICareMapOnboardingStep;
    trackingFrom?: string;
};

export class AddNoteWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IAddNoteDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IAddNoteDrawerProps>){}

    mode = 'add-note';

    Component = AddNoteDrawerView;

    open = (props?: IAddNoteDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };
};


const AddNoteDrawerView: React.FC<IAddNoteDrawerProps> = ({ autoOpenFileChooser, careMapOnboardingStepComplete }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const alert = useInstance(AlertPresenter);

    const noteVM = useSubscriptionSync(() => notesFactory.createFormForNewNote());

    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    const saveAndClose = async () => {
        try {
            const { trackingFrom } = drawerHelper.getDrawerParams();
            await noteVM.save({ trackingFrom });
            drawerHelper.close();

            if (careMapOnboardingStepComplete) {
                careMapState.completeOnboardingStep(careMapOnboardingStepComplete);
            }
        }
        catch (err: any){
            alert.showAndLogError(err);
        }
    };

    return (
        <QueryParamDrivenDrawerPage
            title="Add Note"
            hasBack={false}
            data-type="note-editor"
            buttons={(
                <>
                    <IonButton
                        data-type="save-button"
                        disabled={!noteVM.canSave()}
                        onClick={saveAndClose}>Save</IonButton>
                    <IonButton
                        fill="outline"
                        data-type="cancel-button"
                        onClick={() => {
                            drawerHelper.close();
                        }}
                    >Cancel</IonButton>
                </>
            )}
        >
            <ActiveCaremapNoteCreator
                noteVM={noteVM}
                autoOpenFileChooser={autoOpenFileChooser}
            />
        </QueryParamDrivenDrawerPage>
    );
};
